import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, Mail, AlertCircle, LogIn } from "lucide-react";

const AdminLogin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { signIn, user, isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!authLoading && user && isAdmin) {
      navigate("/admin", { replace: true });
    }
  }, [user, isAdmin, authLoading, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    const { error } = await signIn(email, password);
    if (error) {
      setError("Invalid credentials.");
    }
    setLoading(false);
    // Navigation handled by useEffect once isAdmin resolves
  };

  return (
    <div className="flex min-h-screen items-center justify-center" style={{ background: "hsl(0 0% 4%)" }}>
      <div className="w-full max-w-sm rounded-xl border p-8" style={{ background: "hsl(0 0% 8%)", borderColor: "hsl(0 0% 16%)" }}>
        <div className="mb-6 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full" style={{ background: "hsl(207 68% 50% / 0.15)" }}>
            <Lock size={20} style={{ color: "hsl(207 68% 60%)" }} />
          </div>
          <h1 className="text-xl font-bold" style={{ color: "hsl(0 0% 95%)" }}>Admin Login</h1>
          <p className="mt-1 text-sm" style={{ color: "hsl(0 0% 50%)" }}>Authorized personnel only</p>
        </div>

        {error && (
          <div className="mb-4 flex items-center gap-2 rounded-lg border p-3 text-sm" style={{ background: "hsl(0 70% 50% / 0.1)", borderColor: "hsl(0 70% 50% / 0.3)", color: "hsl(0 70% 70%)" }}>
            <AlertCircle size={16} className="shrink-0" />
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="email" className="text-sm" style={{ color: "hsl(0 0% 70%)" }}>Email</Label>
            <div className="relative mt-1">
              <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "hsl(0 0% 40%)" }} />
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="pl-9 border"
                style={{ background: "hsl(0 0% 6%)", borderColor: "hsl(0 0% 16%)", color: "hsl(0 0% 90%)" }}
              />
            </div>
          </div>
          <div>
            <Label htmlFor="password" className="text-sm" style={{ color: "hsl(0 0% 70%)" }}>Password</Label>
            <div className="relative mt-1">
              <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "hsl(0 0% 40%)" }} />
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="pl-9 border"
                style={{ background: "hsl(0 0% 6%)", borderColor: "hsl(0 0% 16%)", color: "hsl(0 0% 90%)" }}
              />
            </div>
          </div>
          <Button
            type="submit"
            disabled={loading}
            className="w-full flex items-center justify-center gap-2"
            style={{ background: "hsl(207 68% 50%)", color: "white" }}
          >
            <LogIn size={16} />
            {loading ? "Signing in..." : "Sign In"}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default AdminLogin;
